import React, { useEffect, useState } from 'react';
import {
  Autocomplete,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import api from '../../services/api';
import { CallEvent } from '../../types';
import { CALL_SHORTCUTS } from './contact';

type CustomerOption = { id: number; name: string };

interface Props {
  open: boolean;
  onClose: () => void;
  onScheduled?: (call: CallEvent) => void;
  /** Cliente ya elegido (desde la ficha). Si no viene, se busca en el diálogo. */
  customer?: CustomerOption | null;
  /** Fecha sugerida (YYYY-MM-DD), por ejemplo el día clickeado en el calendario. */
  defaultDate?: string;
}

const ScheduleCallDialog: React.FC<Props> = ({ open, onClose, onScheduled, customer, defaultDate }) => {
  const [selected, setSelected] = useState<CustomerOption | null>(customer ?? null);
  const [options, setOptions] = useState<CustomerOption[]>([]);
  const [search, setSearch] = useState('');
  const [loadingOptions, setLoadingOptions] = useState(false);
  const [inDays, setInDays] = useState<number | null>(defaultDate ? null : 30);
  const [date, setDate] = useState(defaultDate || '');
  const [notes, setNotes] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;
    setSelected(customer ?? null);
    setInDays(defaultDate ? null : 30);
    setDate(defaultDate || '');
    setNotes('');
    setError(null);
  }, [open, customer, defaultDate]);

  useEffect(() => {
    if (!open || customer) return;
    const t = setTimeout(async () => {
      setLoadingOptions(true);
      try {
        const res = await api.getCustomers({ page: 1, pageSize: 20, search: search || undefined });
        setOptions((res.customers || []).map((c: any) => ({ id: c.id, name: c.displayName || c.legalName })));
      } catch {
        setOptions([]);
      } finally {
        setLoadingOptions(false);
      }
    }, 250);
    return () => clearTimeout(t);
  }, [search, open, customer]);

  const save = async () => {
    if (!selected) { setError('Elegí el cliente'); return; }
    if (inDays == null && !date) { setError('Elegí cuándo llamar'); return; }
    setSaving(true);
    setError(null);
    try {
      const call = await api.scheduleCall({
        customerId: selected.id,
        inDays: inDays ?? undefined,
        date: inDays == null ? date : undefined,
        notes: notes || undefined,
      });
      onScheduled?.(call);
      onClose();
    } catch (e: any) {
      setError(e?.response?.data?.message || 'No se pudo agendar el llamado');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Agendar llamado</DialogTitle>
      <DialogContent>
        <Stack spacing={2} sx={{ mt: 1 }}>
          {customer ? (
            <Typography variant="subtitle1" fontWeight={600}>{customer.name}</Typography>
          ) : (
            <Autocomplete
              options={options}
              value={selected}
              loading={loadingOptions}
              getOptionLabel={o => o.name}
              isOptionEqualToValue={(a, b) => a.id === b.id}
              onChange={(_, v) => setSelected(v)}
              onInputChange={(_, v) => setSearch(v)}
              filterOptions={x => x}
              renderInput={params => (
                <TextField
                  {...params}
                  label="Cliente"
                  InputProps={{
                    ...params.InputProps,
                    endAdornment: (
                      <>
                        {loadingOptions ? <CircularProgress size={18} /> : null}
                        {params.InputProps.endAdornment}
                      </>
                    ),
                  }}
                />
              )}
            />
          )}

          <Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>¿Cuándo?</Typography>
            <Stack direction="row" flexWrap="wrap" gap={1}>
              {CALL_SHORTCUTS.map(s => (
                <Chip
                  key={s.days}
                  label={s.label}
                  color={inDays === s.days ? 'primary' : 'default'}
                  variant={inDays === s.days ? 'filled' : 'outlined'}
                  onClick={() => { setInDays(s.days); setDate(''); }}
                />
              ))}
            </Stack>
          </Box>
          <TextField
            label="O elegí una fecha"
            type="date"
            value={date}
            onChange={e => { setDate(e.target.value); setInDays(null); }}
            InputLabelProps={{ shrink: true }}
          />
          <TextField
            label="Para qué (opcional)"
            placeholder="Ej: ofrecer reposición, cobrar factura"
            value={notes}
            onChange={e => setNotes(e.target.value)}
            multiline
            minRows={2}
          />
          {error && <Typography color="error" variant="body2">{error}</Typography>}
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancelar</Button>
        <Button variant="contained" onClick={save} disabled={saving}>
          {saving ? 'Guardando…' : 'Agendar'}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ScheduleCallDialog;
